import {
  MessageCircle,
  Phone,
  Search,
  Scale,
  Wallet,
  Check,
  X,
  AlertTriangle,
  Banknote,
  Ticket,
  ShieldCheck,
  Zap,
  HeartHandshake,
  UserCheck,
  Library,
  Package,
  Sparkles,
  Anchor,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/content";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";

const steps = [
  {
    icon: Search,
    step: "01",
    title: "Vor-Ort-Bewertung",
    text: "Wir prüfen gemeinsam den Zustand deiner Karten und Produkte – Kratzer, Abnutzung, Zentrierung, Originalverpackung – und ermitteln den aktuellen Marktwert. Transparent und auf Augenhöhe.",
  },
  {
    icon: Scale,
    step: "02",
    title: "Faire Preisermittlung",
    text: "Basierend auf Zustand, Beliebtheit, Nachfrage und Verfügbarkeit erstellen wir dir ein marktgerechtes Angebot – ohne versteckte Abzüge, nachvollziehbar erklärt.",
  },
  {
    icon: Wallet,
    step: "03",
    title: "Sofortige Auszahlung",
    text: "Du entscheidest: Bargeld bzw. Echtzeit-Überweisung oder Store-Gutschein mit besserer Quote. Direkt vor Ort, ohne Wartezeit.",
  },
];

const buyCategories = [
  {
    icon: Library,
    title: "Sammlungen & Lots",
    text: "Komplette Sammlungen, Alben und sortierte Lots – wir bewerten den Gesamtwert professionell und machen dir ein Angebot für alles auf einmal.",
    kanji: "収集",
  },
  {
    icon: Package,
    title: "Sealed-Produkte",
    text: "Displays, Booster-Boxen, Tins, Elite Trainer Boxen und Special Editions – originalverpackt und ungeöffnet. Keine Einzelbooster.",
    kanji: "未開封",
  },
  {
    icon: Sparkles,
    title: "Pokémon-Karten",
    text: "Egal ob alt oder neu: von Vintage-Klassikern bis zu aktuellen Sets. Einzelkarten bevorzugt in gutem Zustand (Near Mint oder besser).",
    kanji: "ポケモン",
  },
  {
    icon: Anchor,
    title: "One Piece TCG",
    text: "Aktuelle One Piece-Karten und gefragte Singles aus den neuesten Sets – Meta-Karten, Leader und Raritaten.",
    kanji: "海賊",
  },
];

const noBuyList = [
  "Bulk-Ware: große Mengen gängiger Karten",
  "Niedrigpreisige Einzelkarten",
  "Einzelbooster (Sealed nur als Display, Box oder Tin)",
  "Karten mit starken Gebrauchsspuren oder Fälschungsverdacht",
];

const whyUs = [
  { icon: Zap, title: "Schnell", text: "Bewertung & Auszahlung in einem Termin – ohne Versand, ohne Risiko." },
  { icon: ShieldCheck, title: "Transparent", text: "Wir erklären dir jede Bewertung nachvollziehbar am Marktpreis." },
  { icon: HeartHandshake, title: "Fair", text: "Marktgerechte Angebote statt Lowball – wir wollen, dass du wiederkommst." },
  { icon: UserCheck, title: "Fachkundig", text: "Persönliche Beratung von Sammlern für Sammler." },
];

const faqs = [
  {
    q: "Welche Pokémon Karten kauft ihr an?",
    a: "Wir kaufen Pokémon-Einzelkarten, komplette Sammlungen und Sealed-Produkte an – egal ob Vintage-Klassiker oder Karten aus aktuellen Sets. Bulk (große Mengen Common/Uncommon) nur nach Absprache.",
  },
  {
    q: "Kauft ihr ganze Sammlungen an?",
    a: "Ja, wir kaufen auch komplette Sammlungen an. Bring sie einfach während unserer Öffnungszeiten vorbei – bei größeren Sammlungen ist ein kurzer Anruf vorab sinnvoll, damit wir uns Zeit nehmen können.",
  },
  {
    q: "Muss ich vorher einen Termin machen?",
    a: "Für kleinere Mengen nicht – komm einfach während der Öffnungszeiten vorbei. Bei großen Sammlungen empfehlen wir einen kurzen Anruf vorab unter 0221 96260815.",
  },
  {
    q: "Wie wird der Preis berechnet?",
    a: "Wir bewerten deine Karten transparent am aktuellen Marktwert und erklären dir jede Bewertung nachvollziehbar. Faktoren sind u. a. Karte, Set, Seltenheit, Zustand und aktuelle Nachfrage.",
  },
  {
    q: "Kauft ihr auch japanische Karten?",
    a: "Ja, japanische Karten sind bei Sammlern sehr beliebt und wir kaufen sie ebenfalls an. Bring sie gern mit – wir bewerten sie gemeinsam mit dir.",
  },
  {
    q: "Kauft ihr beschädigte Karten?",
    a: "Bevorzugt kaufen wir Karten in gutem Zustand (Near Mint). Beschädigte oder stark gespielte Karten bewerten wir entsprechend dem Zustand – sprich uns einfach darauf an.",
  },
  {
    q: "Gibt es mehr bei Auszahlung als Store-Gutschein?",
    a: "Ja. Bei Auszahlung in bar bzw. per Echtzeit-Überweisung erhältst du 60–90 % des Wertes, beim Store-Gutschein die bessere Quote von 70–100 %.",
  },
  {
    q: "Kauft ihr auch One Piece und Yu-Gi-Oh! Karten an?",
    a: "Ja, neben Pokémon kaufen wir auch One Piece, Yu-Gi-Oh! und weitere gefragte TCG-Karten an – Einzelkarten wie Sammlungen.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Ankauf() {
  return (
    <div data-testid="ankauf-page">
      <Seo
        title="Pokémon Karten Ankauf Köln | TCG Karten verkaufen bei Shibuya"
        description="Verkaufe deine Pokémon, One Piece, Yu-Gi-Oh! und TCG Karten direkt vor Ort in Köln. Faire Bewertung, schneller Ankauf und Store-Gutschein möglich. Zülpicher Str. 357, 50935 Köln."
        path="/ankauf"
        keywords="Pokémon Karten Ankauf Köln, TCG Karten Ankauf Köln, Pokémon Karten verkaufen Köln, One Piece Karten verkaufen, Yu-Gi-Oh Karten verkaufen Köln, Sammlung verkaufen Köln, Sealed Ankauf"
      />
      <JsonLd id="ankauf-faq" data={faqSchema} />
      {/* ============ HERO ============ */}
      <section className="hero-glow noise relative overflow-hidden border-b border-border">
        <div
          aria-hidden="true"
          className="neon-flicker pointer-events-none absolute right-8 top-1/2 z-10 hidden -translate-y-1/2 select-none lg:block"
        >
          <p className="neon-kanji kanji-vertical text-4xl">買取中</p>
        </div>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-6 top-0 select-none font-jp text-[140px] font-black leading-none text-white/[0.04] md:text-[220px]"
        >
          買取
        </div>
        <div className="relative z-10 mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <Badge
              variant="outline"
              className="mb-6 rounded-full border-accent/40 bg-accent/10 px-4 py-1.5 font-mono-ui text-[11px] uppercase tracking-widest text-accent"
            >
              Professioneller TCG-Ankauf
            </Badge>
            <h1 className="max-w-3xl font-display text-5xl uppercase leading-[0.95] tracking-[0.04em] sm:text-6xl lg:text-7xl">
              Pokémon &amp; TCG
              <br />
              Karten-Ankauf <span className="neon-text">in Köln.</span>
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Verkaufe deine Pokémon-, One-Piece-, Yu-Gi-Oh!- und weiteren
              TCG-Karten direkt vor Ort in Köln. Wir kaufen Einzelkarten,
              komplette Sammlungen und Sealed-Produkte an – mit fairer Bewertung,
              schnellem Ankauf und optionalem Store-Gutschein.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                data-testid="ankauf-hero-phone-button"
                className="h-12 rounded-xl bg-primary px-6 font-medium text-primary-foreground shadow-[0_10px_30px_hsl(22_100%_50%/0.35)] transition-shadow hover:shadow-[0_14px_44px_hsl(22_100%_50%/0.5)]"
              >
                <a href={CONTACT.phoneHref}>
                  <Phone className="mr-2 h-5 w-5" /> {CONTACT.phoneDisplay}
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="ankauf-hero-whatsapp-button"
                className="h-12 rounded-xl border-primary/30 bg-transparent px-6 hover:bg-primary/10"
              >
                <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-5 w-5" /> Per WhatsApp anfragen
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ WAS WIR ANKAUFEN ============ */}
      <section className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <div aria-hidden="true" className="halftone halftone-fade pointer-events-none absolute inset-x-0 top-0 h-48" />
        <Reveal>
          <div className="flex items-center gap-3">
            <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">
              Unser Ankaufsportfolio
            </p>
            <span aria-hidden="true" className="neon-kanji-orange text-sm">買取品</span>
          </div>
          <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
            Das kaufen wir <span className="neon-text">an.</span>
          </h2>
        </Reveal>
        <div data-testid="ankauf-buy-list" className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {buyCategories.map((cat, i) => (
            <Reveal key={cat.title} delay={i * 0.06}>
              <div
                data-testid={`ankauf-buy-${i + 1}`}
                className="neon-card noise manga-frame relative h-full overflow-hidden rounded-2xl border border-primary/25 bg-card p-6"
              >
                <span aria-hidden="true" className="neon-kanji-orange absolute right-4 top-4 text-lg opacity-60">
                  {cat.kanji}
                </span>
                <div className="relative z-10">
                  <cat.icon className="h-6 w-6 text-primary" />
                  <p className="mt-4 font-display text-2xl uppercase leading-tight tracking-wide">
                    {cat.title}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {cat.text}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Was wir nicht kaufen */}
        <Reveal delay={0.1}>
          <div
            data-testid="ankauf-no-buy-list"
            className="mt-10 rounded-2xl border border-border bg-card p-6 md:p-8"
          >
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="max-w-xl">
                <p className="font-display text-2xl uppercase tracking-wide text-muted-foreground">
                  Davon sehen wir ab
                </p>
                <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {noBuyList.map((item) => (
                    <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-destructive/15">
                        <X className="h-3 w-3 text-destructive" />
                      </span>
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <Alert
                data-testid="ankauf-disclaimer"
                className="max-w-md border-border bg-background/60"
              >
                <AlertTriangle className="h-4 w-4 text-accent" />
                <AlertTitle className="text-sm font-medium">Gut zu wissen</AlertTitle>
                <AlertDescription className="text-xs leading-relaxed text-muted-foreground">
                  Eine vorsortierte Sammlung (nach Seltenheit oder Edition)
                  beschleunigt die Bewertung deutlich. Wir behalten uns vor,
                  Karten oder Produkte ohne Angabe von Gründen abzulehnen.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============ PROZESS ============ */}
      <section className="relative overflow-hidden border-y border-border bg-card/50">
        <div aria-hidden="true" className="speedlines pointer-events-none absolute inset-0 opacity-60" />
        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3">
              <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">
                So läuft's ab
              </p>
              <span aria-hidden="true" className="neon-kanji-orange text-sm">手順</span>
            </div>
            <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
              In 3 Schritten zu deinem <span className="neon-text">Geld.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {steps.map((step, i) => (
              <Reveal key={step.step} delay={i * 0.08}>
                <div
                  data-testid={`ankauf-step-${i + 1}`}
                  className="noise relative h-full overflow-hidden rounded-2xl border border-border bg-card p-6 neon-card"
                >
                  <div className="relative z-10">
                    <div className="flex items-center justify-between">
                      <step.icon className="h-6 w-6 text-primary" />
                      <span className="font-display text-4xl text-white/10">{step.step}</span>
                    </div>
                    <p className="mt-4 font-display text-2xl uppercase tracking-wide">
                      {step.title}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {step.text}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AUSZAHLUNG ============ */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <Reveal>
          <div className="flex items-center gap-3">
            <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">
              Deine Auszahlung
            </p>
            <span aria-hidden="true" className="neon-kanji-orange text-sm">支払い</span>
          </div>
          <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
            Du hast die <span className="neon-text">Wahl.</span>
          </h2>
        </Reveal>
        <div
          data-testid="ankauf-payout-comparison"
          className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2"
        >
          <Reveal delay={0.05}>
            <div
              data-testid="ankauf-payout-cash-card"
              className="noise manga-frame relative h-full overflow-hidden rounded-2xl border border-border bg-card p-6 md:p-8"
            >
              <div className="relative z-10">
                <Banknote className="h-7 w-7 text-primary" />
                <p className="mt-4 font-display text-3xl uppercase tracking-wide">
                  Bargeld / Überweisung
                </p>
                <p className="mt-2 font-display text-6xl text-primary">60–90%</p>
                <p className="mt-1 font-mono-ui text-[11px] uppercase tracking-widest text-muted-foreground">
                  des ermittelten Wertes
                </p>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  Sofortige Barzahlung oder Echtzeit-Überweisung – das Geld ist
                  da, bevor du den Store verlässt.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div
              data-testid="ankauf-payout-store-credit-card"
              className="noise manga-frame relative h-full overflow-hidden rounded-2xl border border-accent/40 bg-card p-6 md:p-8"
            >
              <Badge className="absolute right-5 top-5 rounded-full bg-accent font-mono-ui text-[10px] uppercase tracking-wider text-accent-foreground">
                Beste Quote
              </Badge>
              <div className="relative z-10">
                <Ticket className="h-7 w-7 text-accent" />
                <p className="mt-4 font-display text-3xl uppercase tracking-wide">
                  Store-Gutschein
                </p>
                <p className="mt-2 font-display text-6xl text-accent">70–100%</p>
                <p className="mt-1 font-mono-ui text-[11px] uppercase tracking-widest text-muted-foreground">
                  des ermittelten Wertes
                </p>
                <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                  Mehr Wert für dein Hobby: Der Gutschein ist flexibel bei uns
                  einlösbar – für Karten, Figuren, Zubehör und mehr.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ WARUM WIR ============ */}
      <section className="border-y border-border bg-card/50">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3">
              <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">
                Warum bei uns verkaufen?
              </p>
              <span aria-hidden="true" className="neon-kanji-orange text-sm">信頼</span>
            </div>
            <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
              Von Sammlern. Für <span className="neon-text">Sammler.</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyUs.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.06}>
                <div className="neon-card h-full rounded-2xl border border-border bg-card p-6">
                  <item.icon className="h-6 w-6 text-primary" />
                  <p className="mt-4 font-display text-2xl uppercase tracking-wide">
                    {item.title}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {item.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section className="border-y border-border bg-card/50">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3">
              <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">
                Häufige Fragen
              </p>
              <span aria-hidden="true" className="neon-kanji-orange text-sm">質問</span>
            </div>
            <h2 className="mt-3 font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
              Pokémon-Karten verkaufen in <span className="neon-text">Köln.</span>
            </h2>
          </Reveal>
          <div data-testid="ankauf-faq" className="mt-10 space-y-4">
            {faqs.map((faq, i) => (
              <Reveal key={faq.q} delay={i * 0.05}>
                <details
                  data-testid={`ankauf-faq-${i + 1}`}
                  className="group neon-card rounded-2xl border border-border bg-card p-5 md:p-6"
                >
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4">
                    <h3 className="font-display text-xl uppercase tracking-wide text-foreground md:text-2xl">
                      {faq.q}
                    </h3>
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full border border-primary/40 text-primary transition-transform group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground md:text-base">
                    {faq.a}
                  </p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INTERNE LINKS ============ */}
      <section className="mx-auto max-w-6xl px-4 pt-16 sm:px-6 md:pt-24">
        <Reveal>
          <p className="font-mono-ui text-xs uppercase tracking-[0.25em] text-primary">Mehr entdecken</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {[
              { to: "/pokemon-karten-koeln", label: "Pokémon Karten Köln" },
              { to: "/one-piece-karten-koeln", label: "One Piece Karten Köln" },
              { to: "/yugioh-karten-koeln", label: "Yu-Gi-Oh! Karten Köln" },
              { to: "/anfahrt", label: "Anfahrt & Öffnungszeiten" },
              { to: "/kontakt", label: "Kontakt" },
            ].map((r) => (
              <Link key={r.to} to={r.to} data-testid={`ankauf-related-${r.to}`} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
                {r.label}
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ============ CTA STRIP ============ */}
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-24">
        <Reveal>
          <div className="noise relative overflow-hidden rounded-2xl border border-border bg-card p-8 text-center md:p-14">
            <div className="cta-glow pointer-events-none absolute inset-0" />
            <div className="relative z-10">
              <h2 className="font-display text-4xl uppercase tracking-[0.04em] sm:text-5xl">
                Bereit? Komm einfach <span className="neon-text">vorbei.</span>
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground md:text-base">
                Bring deine Karten während unserer Öffnungszeiten in den Store –
                wir freuen uns auf dich und deine Sammlung. Fragen vorab? Ruf
                uns einfach an.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Button
                  asChild
                  size="lg"
                  data-testid="ankauf-cta-phone-button"
                  className="h-12 rounded-xl bg-primary px-6 font-medium text-primary-foreground shadow-[0_10px_30px_hsl(22_100%_50%/0.35)] transition-shadow hover:shadow-[0_14px_44px_hsl(22_100%_50%/0.5)]"
                >
                  <a href={CONTACT.phoneHref}>
                    <Phone className="mr-2 h-5 w-5" /> Jetzt anrufen
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  data-testid="ankauf-cta-whatsapp-button"
                  className="h-12 rounded-xl border-primary/30 bg-transparent px-6 hover:bg-primary/10"
                >
                  <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
